import {
  StyledDialog,
  useObservable,
  useFormEvents,
  SubmitButton,
  FormHub,
  ErrorDisplay,
  useHasRole,
  Feedback,
} from '@lib/common';
import {
  FormStatus,
  roomsQuery,
  roomsService,
  Properties,
  Manager,
  Admin,
  Maintenance,
  reservationQuery,
  reservationService,
  Reservation,
  Name,
} from '@lib/state';
import {
  DialogContent,
  Tabs,
  Tab,
  Box,
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  makeStyles,
  createStyles,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import { TabContext, TabPanel } from '@material-ui/lab';
import { AuditLogs } from 'app/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  employeeQuery,
  employeeService,
  MaintenanceTicket,
  MaintenanceTicketForms,
  maintenanceTicketQuery,
  maintenanceTicketService,
} from 'app/state';
import { MaintenanceTicketForm } from './maintenance-ticket-form';
import { MaintenanceTicketPhotoUpload } from './maintenance-ticket-photo-upload';
import { MaintenanceJournal } from './maintenance-journal';
import { parse } from 'date-fns';

export interface MaintenanceTicketFormFields {
  status: Properties.MaintenanceTicketStatusEnum;
  priority: Properties.PriorityOptions;
  roomId?: string | null;
  area?: string;
  assignedTo?: string | null;
  description?: string | null;
  isActive?: boolean;
  categoryId?: number | null;
  resolutionDescription?: string | null;
  userCategory?: string | null;
}

interface Props {
  propertyId: string;
  onClose: () => void;
  open: boolean;
}

const useStyles = makeStyles(theme =>
  createStyles({
    fullWidth: {
      width: '100%',
    },
  })
);

export const MaintenanceTicketModal: React.FC<Props> = ({ propertyId, open, onClose }) => {
  const [{ status, error }, resetUI] = useFormEvents(MaintenanceTicketForms.Manage);
  const [{ status: deleteStatus, error: deleteError }] = useFormEvents(
    MaintenanceTicketForms.Delete
  );
  const [{ status: grantAccessStatus, error: grantAccessError }, grantResetUI] = useFormEvents(
    MaintenanceTicketForms.GrantAccess
  );
  const styles = useStyles();

  const reservations = useObservable(reservationQuery.reservations);
  const existingPhotos = useObservable(maintenanceTicketQuery.photos);
  const entryCode = useObservable(maintenanceTicketQuery.entryCode);

  const ticket = useObservable(maintenanceTicketQuery.selectedTicket);

  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(
    ticket?.roomId ?? undefined
  );

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(
    ticket?.assignedTo?.id ?? undefined
  );
  const [area, setArea] = useState<string | undefined>(ticket?.area ?? undefined);

  const [selectedReservation, setReservation] = useState<Reservation | undefined>(undefined);

  const hasManagerRole = useHasRole(Admin, Manager);
  const hasMaintenanceRole = useHasRole(Maintenance);
  const employees = useObservable(employeeQuery.maintenanceEligibleEmployees);

  const selectedEmployee = useMemo(() => {
    if (!ticket?.assignedTo) return undefined;
    return employees.find(e => e.userId === ticket.assignedTo?.id);
  }, [employees, ticket]);

  useEffect(() => {
    if (ticket?.id) {
      maintenanceTicketService.getMaintenanceTicketPhotos(ticket.id);
      setSelectedEmployeeId(ticket.assignedTo?.id);
      if (ticket.roomId) setSelectedRoomId(ticket.roomId);
    }
  }, [ticket]);

  useEffect(() => {
    if (selectedRoomId) reservationService.getReservationByRoomId(selectedRoomId);
  }, [selectedRoomId]);

  useEffect(() => {
    if (!!reservations?.length && selectedRoomId)
      setReservation(reservations.filter(x => x.rooms.some(f => f.roomId === selectedRoomId))?.[0]);
  }, [reservations, selectedRoomId]);

  const defaultValues: MaintenanceTicketFormFields = useMemo(
    () => ({
      roomId: ticket?.roomId,
      status: ticket?.status ?? Properties.MaintenanceTicketStatusEnum.Open,
      priority: ticket?.priority ?? Properties.PriorityOptions.Normal,
      area: ticket?.area ?? '',
      assignedTo: ticket?.assignedTo?.id ?? '',
      categoryId: ticket?.categoryId ?? undefined,
      resolutionDescription: ticket?.resolutionDescription ?? '',
      userCategory: ticket?.userCategory ?? '',
    }),
    [ticket]
  );

  useEffect(() => {
    roomsService.getRooms(propertyId);
    employeeService.getEmployees({ propertyId });
  }, [propertyId]);

  useEffect(() => {
    if (open) maintenanceTicketService.loadCategories();
  }, [open]);

  const rooms = useObservable(roomsQuery.rooms);
  const categories = useObservable(maintenanceTicketQuery.categories);

  const submit = useCallback(
    (fields: MaintenanceTicketFormFields) => {
      grantResetUI();
      // Gap 9 — a category soft-deleted after the ticket was saved is excluded from the loaded
      // picker tree; submitting its id verbatim trips the backend category guard and 500s. Drop
      // any id not present in the loaded tree, and only carry userCategory alongside a valid id.
      const categoryIsLoaded =
        fields.categoryId != null &&
        categories.some(h => (h.children ?? []).some(c => c.categoryId === fields.categoryId));
      const safeCategoryId = categoryIsLoaded ? fields.categoryId ?? undefined : undefined;
      const userCategory = safeCategoryId != null ? fields.userCategory?.trim() ?? '' : '';
      if (!ticket?.id) {
        maintenanceTicketService.createMaintenanceTicket({
          propertyId: propertyId,
          priority: fields.priority,
          roomId: fields.roomId,
          area: fields.area,
          description: fields.description,
          assignedTo: fields.assignedTo === '' ? undefined : fields.assignedTo,
          categoryId: safeCategoryId,
          userCategory: userCategory,
        });
      } else {
        // A category soft-deleted after the ticket was saved is excluded from the loaded
        // picker tree by the backend's query filter. Submitting it verbatim trips the
        // backend's category-guard and surfaces as a 500 — coerce to undefined instead.
        const categoryIsLoaded =
          fields.categoryId != null &&
          categories.some(h => (h.children ?? []).some(c => c.categoryId === fields.categoryId));
        const safeCategoryId = categoryIsLoaded ? fields.categoryId ?? undefined : undefined;
        maintenanceTicketService.updateMaintenanceTicket(ticket, {
          propertyId: propertyId,
          priority: fields.priority,
          roomId: fields.roomId,
          status: fields.status,
          area: fields.area,
          assignedTo: fields.assignedTo === '' ? undefined : fields.assignedTo,
          isActive: fields.isActive,
          categoryId: safeCategoryId,
          resolutionDescription:
            (fields.resolutionDescription?.trim() ?? '') === ''
              ? undefined
              : fields.resolutionDescription,
          userCategory: userCategory,
        });
      }
    },
    [propertyId, ticket, grantResetUI, categories]
  );

  const deleteTicket = useCallback(() => {
    maintenanceTicketService.deleteMaintenanceTicket(ticket);
  }, [ticket]);

  const deletePhoto = useCallback(
    (photoId: string) => {
      maintenanceTicketService.deleteMaintenanceTicketPhoto(ticket.id, photoId);
    },
    [ticket]
  );

  const grantRoomAccess = useCallback(() => {
    if (ticket.roomId && ticket.roomNumber) {
      maintenanceTicketService.grantMaintenanceTicketAccess(propertyId, ticket);
    }
  }, [ticket, propertyId]);

  useEffect(() => {
    if (deleteStatus === FormStatus.Success) onClose();
  }, [deleteStatus, onClose]);

  const accessButtonStatus = useMemo(
    () =>
      getAccessButtonStatus({
        status,
        grantAccessStatus,
        grantAccessError,
        ticket,
        selectedEmployeeId,
        selectedRoomId,
        entryCode,
        selectedEmployee,
      }),
    [
      status,
      grantAccessStatus,
      grantAccessError,
      ticket,
      selectedEmployeeId,
      selectedRoomId,
      entryCode,
      selectedEmployee,
    ]
  );

  const [tab, setTab] = useState('ticket');

  return (
    <>
      <StyledDialog
        title={ticket?.id ? `Ticket ${ticket.ticketNumber}` : 'Create Ticket'}
        onClose={onClose}
        open={open}
        maxWidth="lg"
        scroll="paper"
      >
        <TabContext value={tab}>
          <Tabs
            value={tab}
            onChange={(_, x) => setTab(x)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Details" value="ticket" />
            {!!ticket?.id && <Tab label="Notes" value="notes" />}
            {!!ticket?.id && <Tab label="Photos" value="photos" />}
            {!!ticket?.id && <Tab label="History" value="history" />}
          </Tabs>
          <DialogContent>
            <TabPanel value="ticket">
              <ErrorDisplay error={error ?? deleteError} />
              <FormHub onSubmit={submit} formOptions={{ defaultValues }}>
                <MaintenanceTicketForm
                  ticket={ticket}
                  rooms={rooms}
                  employees={employees}
                  hasManagerRole={hasManagerRole}
                  hasMaintenanceRole={hasMaintenanceRole}
                  setSelectedRoomId={setSelectedRoomId}
                  setSelectedEmployeeId={setSelectedEmployeeId}
                  selectedRoomId={selectedRoomId}
                  setArea={setArea}
                  area={area}
                  selectedReservation={selectedReservation}
                  categories={categories}
                  propertyId={propertyId}
                />
                <Feedback show={status === FormStatus.Success} severity="success" onHide={resetUI}>
                  Maintenance ticket saved successfully!
                </Feedback>
                <Box mt={2} display="flex">
                  {ticket && hasManagerRole && false && (
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!!deleteStatus && deleteStatus === FormStatus.Pending}
                      fullWidth
                      style={{ margin: '0 6px 0 0', opacity: '60%' }}
                      onClick={deleteTicket}
                    >
                      Delete
                    </Button>
                  )}
                  <SubmitButton
                    variant="contained"
                    color="primary"
                    disabled={!!status && status === FormStatus.Pending}
                    fullWidth
                  >
                    Save
                  </SubmitButton>
                </Box>

                <Box mt={2} display="flex" flexDirection="column">
                  <Typography variant="subtitle1">
                    {accessButtonStatus.text}&nbsp;
                    {accessButtonStatus.generateAccessCode && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          grantRoomAccess();
                        }}
                      >
                        Generate Access Code
                      </Button>
                    )}
                  </Typography>
                </Box>
              </FormHub>
            </TabPanel>
            <TabPanel value="photos">
              <Box mt={2} display="flex">
                <ImageList cols={3} className={styles.fullWidth}>
                  {existingPhotos &&
                    existingPhotos.map(item => {
                      return (
                        <ImageListItem key={item.id}>
                          <img src={item.url} alt={item.id} loading="lazy" />
                          <ImageListItemBar
                            actionIcon={
                              <IconButton color="secondary" onClick={() => deletePhoto(item.id)}>
                                <DeleteIcon />
                              </IconButton>
                            }
                          />
                        </ImageListItem>
                      );
                    })}
                </ImageList>
              </Box>
              <Box mt={2} display="flex">
                <MaintenanceTicketPhotoUpload ticket={ticket} />
              </Box>
            </TabPanel>
            {ticket && (
              <TabPanel value="history">
                <AuditLogs scope="maintenance-ticket" id={ticket.id} />
              </TabPanel>
            )}
            {ticket && (
              <TabPanel value="notes">
                <MaintenanceJournal ticketId={ticket.id} propertyId={ticket.propertyId} />
              </TabPanel>
            )}
          </DialogContent>
        </TabContext>
      </StyledDialog>
    </>
  );
};
export const getAccessButtonStatus = (params: {
  status: FormStatus;
  grantAccessStatus: FormStatus;
  grantAccessError: any;
  ticket?: MaintenanceTicket;
  selectedEmployeeId?: string;
  selectedRoomId?: string;
  entryCode: any;
  selectedEmployee: any;
}) => {
  const {
    status,
    grantAccessStatus,
    grantAccessError,
    ticket,
    selectedEmployeeId,
    selectedRoomId,
    entryCode,
    selectedEmployee,
  } = params;

  if (
    status === FormStatus.Pending ||
    grantAccessStatus === FormStatus.Pending ||
    !ticket?.id ||
    ticket.assignedTo?.id !== selectedEmployeeId ||
    ticket.roomId !== selectedRoomId
  ) {
    return { text: '', generateAccessCode: false };
  }

  if (grantAccessError) {
    return {
      text: `Error granting access: ${grantAccessError.message}`,
      generateAccessCode: false,
    };
  }

  const isValid =
    entryCode &&
    !!entryCode.validUntil &&
    parseUtcStringToLocalDate(entryCode.validUntil) > new Date();

  if (isValid) {
    return {
      text: `${getEmployeeName(selectedEmployee?.name)} has been granted one-time access to room ${
        ticket.roomNumber
      }.`,
      generateAccessCode: false,
    };
  }

  const allowRegenerate =
    ticket.isActive === true &&
    ticket.status !== Properties.MaintenanceTicketStatusEnum.Closed &&
    ticket.status !== Properties.MaintenanceTicketStatusEnum.Completed &&
    !ticket.deletedOn;

  if (allowRegenerate && !!selectedEmployee?.name) {
    return {
      text: `${getEmployeeName(selectedEmployee?.name)} access has been revoked to room ${
        ticket.roomNumber
      }.`,
      generateAccessCode: true,
    };
  }

  return { text: '', generateAccessCode: false };
};

export const getEmployeeName = (name: Name | undefined): string => {
  return name ? `${name.first} ${name.last}` : '';
};

export const parseUtcStringToLocalDate = (dateStr: string): Date => {
  const cleaned = dateStr.replace(/(\.\d{3})\d+/, '$1');
  const isoLike = cleaned.replace(' ', 'T');
  return parse(isoLike, "yyyy-MM-dd'T'HH:mm:ss.SSS", new Date());
};
